import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';

import {
  createImageUpload,
  handleImageDrop,
  handleImagePaste,
  UploadImagesPlugin,
  type UploadFn,
} from '../plugins/upload-images';
import { blobUrlCache } from '../utils/blob-cache';
import {
  resolveFileId,
  stripImageSrcsForStorage,
  uploadEditorImage,
  type FileIdResolver,
} from '../utils/image';
import { getChangedRanges } from '../utils/transactions';

import type { Node as PmNode } from '@tiptap/pm/model';

const MAX_IMAGE_SIZE = 20 * 1024 * 1024;

function findImagesByFileId(
  editor: Editor,
  fileId: string
): Array<{ pos: number; node: PmNode }> {
  const matches: Array<{ pos: number; node: PmNode }> = [];
  editor.state.doc.descendants((node: PmNode, pos: number) => {
    if (node.type.name === 'image' && node.attrs?.fileId === fileId) {
      matches.push({ pos, node });
    }
    return true;
  });
  return matches;
}

function resolveFileIdImages(
  editor: Editor,
  resolveImageFileId: FileIdResolver,
  resolvingFileIds?: Set<string>
) {
  const { doc } = editor.state;
  const toResolve: string[] = [];

  doc.descendants((node: PmNode) => {
    if (node.type.name === 'image' && node.attrs.fileId && !node.attrs.src) {
      toResolve.push(node.attrs.fileId as string);
    }
  });

  for (const fileId of toResolve) {
    if (resolvingFileIds?.has(fileId)) continue;
    resolvingFileIds?.add(fileId);
    resolveImageFileId(fileId)
      .then((blobUrl) => {
        if (editor.isDestroyed) {
          if (!blobUrlCache.hasUrl(blobUrl)) {
            URL.revokeObjectURL(blobUrl);
          }
          return;
        }
        const matches = findImagesByFileId(editor, fileId);
        if (matches.length === 0) {
          if (!blobUrlCache.hasUrl(blobUrl)) {
            URL.revokeObjectURL(blobUrl);
          }
          return;
        }
        const toUpdate = matches.filter((match) => !match.node.attrs?.src);
        if (toUpdate.length === 0) {
          if (!blobUrlCache.hasUrl(blobUrl)) {
            URL.revokeObjectURL(blobUrl);
          }
          return;
        }

        const tr = editor.state.tr;
        for (const { node, pos: currentPos } of toUpdate) {
          tr.setNodeMarkup(currentPos, undefined, {
            ...node.attrs,
            src: blobUrl,
          });
        }
        editor.view.dispatch(tr);
      })
      .catch((err) => {
        if (editor.isDestroyed) return;
        console.error('[image-upload] failed to resolve fileId:', fileId, err);
      })
      .finally(() => {
        resolvingFileIds?.delete(fileId);
      });
  }
}

export interface ImageUploadExtensionOptions {
  entity?: string;
  entityId?: string;
  resolveImageFileId?: FileIdResolver;
}

function isImageFile(file: File) {
  return file.type.startsWith('image/');
}

function isBlobUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('blob:');
}

function collectBlobUrls(editor: Editor) {
  const urls = new Set<string>();

  editor.state.doc.descendants((node: PmNode) => {
    if (node.type.name === 'image' && isBlobUrl(node.attrs?.src)) {
      const url = node.attrs.src as string;
      if (blobUrlCache.hasUrl(url)) {
        blobUrlCache.touchByUrl(url);
        return;
      }
      urls.add(url);
    }
  });

  return urls;
}

function syncBlobUrls(editor: Editor, trackedUrls: Set<string>) {
  const currentUrls = collectBlobUrls(editor);

  for (const url of trackedUrls) {
    if (!currentUrls.has(url)) {
      if (!blobUrlCache.hasUrl(url)) {
        URL.revokeObjectURL(url);
      }
    }
  }

  trackedUrls.clear();

  for (const url of currentUrls) {
    trackedUrls.add(url);
  }
}

function scheduleImageScan(editor: Editor, storage: ImageUploadStorage) {
  if (storage.scanRafId !== null) return;

  storage.scanRafId = window.requestAnimationFrame(() => {
    storage.scanRafId = null;
    resolveFileIdImages(
      editor,
      storage.resolveImageFileId,
      storage.resolvingFileIds
    );
    syncBlobUrls(editor, storage.blobUrls);
  });
}

function rangeHasImages(doc: PmNode, from: number, to: number) {
  let hasImage = false;
  doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'image') {
      hasImage = true;
      return false;
    }
    return true;
  });
  return hasImage;
}

function clampRange(range: { from: number; to: number }, doc: PmNode) {
  const max = doc.content.size;
  const from = Math.max(0, Math.min(max, range.from));
  const to = Math.max(0, Math.min(max, range.to));
  if (from >= to) return null;
  return { from, to };
}

function shouldScanForImages(transaction: Transaction) {
  if (!transaction.docChanged) return false;
  const changedRanges = getChangedRanges([transaction]);
  if (changedRanges.length === 0) return false;
  return changedRanges.some((range) => {
    const beforeRange = clampRange(range, transaction.before);
    if (
      beforeRange &&
      rangeHasImages(transaction.before, beforeRange.from, beforeRange.to)
    ) {
      return true;
    }
    const afterRange = clampRange(range, transaction.doc);
    if (
      afterRange &&
      rangeHasImages(transaction.doc, afterRange.from, afterRange.to)
    ) {
      return true;
    }
    return false;
  });
}

interface ImageUploadStorage {
  uploadFn: UploadFn | null;
  resolveImageFileId: FileIdResolver;
  resolvingFileIds: Set<string>;
  blobUrls: Set<string>;
  scanRafId: number | null;
}

export const ImageUploadExtension =
  Extension.create<ImageUploadExtensionOptions>({
    name: 'imageUpload',

    addOptions() {
      return {
        entity: undefined,
        entityId: undefined,
        resolveImageFileId: undefined,
      };
    },

    addStorage(): ImageUploadStorage {
      return {
        uploadFn: null,
        resolveImageFileId: resolveFileId,
        resolvingFileIds: new Set<string>(),
        blobUrls: new Set<string>(),
        scanRafId: null,
      };
    },

    onCreate() {
      const { entity, entityId, resolveImageFileId } = this.options;

      const storage = this.storage as ImageUploadStorage;
      storage.resolveImageFileId = resolveImageFileId ?? resolveFileId;
      storage.uploadFn = createImageUpload({
        validateFn: (file) => {
          if (!isImageFile(file)) return false;
          if (file.size > MAX_IMAGE_SIZE) return false;
          return true;
        },
        onUpload: (file) => uploadEditorImage(file, entity, entityId),
      });

      scheduleImageScan(this.editor, storage);

      // Wrap getJSON so persisted output never contains transient blob URLs
      const originalGetJSON = this.editor.getJSON.bind(this.editor);
      this.editor.getJSON = (() =>
        stripImageSrcsForStorage(originalGetJSON())) as typeof originalGetJSON;
    },

    onUpdate({ transaction }) {
      const storage = this.storage as ImageUploadStorage;
      if (!shouldScanForImages(transaction)) return;
      scheduleImageScan(this.editor, storage);
    },

    onDestroy() {
      const storage = this.storage as ImageUploadStorage;
      if (storage.scanRafId !== null) {
        window.cancelAnimationFrame(storage.scanRafId);
        storage.scanRafId = null;
      }
      const urls = storage.blobUrls;
      if (!urls) return;
      for (const url of urls) {
        if (!blobUrlCache.hasUrl(url)) {
          URL.revokeObjectURL(url);
        }
      }
      urls.clear();
    },

    addProseMirrorPlugins() {
      const storage = this.storage as { uploadFn: UploadFn | null };

      return [
        UploadImagesPlugin({
          imageClass: 'animate-pulse [animation-duration:1s]',
        }),
        new Plugin({
          key: new PluginKey('image-upload-handlers'),
          props: {
            handlePaste: (view, event) => {
              if (!storage.uploadFn) return false;
              return handleImagePaste(
                view,
                event as ClipboardEvent,
                storage.uploadFn
              );
            },
            handleDrop: (view, event, _slice, moved) => {
              if (!storage.uploadFn) return false;
              return handleImageDrop(
                view,
                event as DragEvent,
                moved,
                storage.uploadFn
              );
            },
          },
        }),
      ];
    },
  });
